import { useQuery, useMutation } from '@tanstack/react-query'
import { UsersFilterParams, CreateUserParams, UpdateUserParams, SendEmailVerificationParams } from '@/types/user'
import { useUsersStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useUsers = (params: UsersFilterParams) => {
  const fetchUsers = useUsersStore(state => state.fetchUsers)

  return useQuery({
    queryKey: ['users', params],
    queryFn: () => fetchUsers(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useUser = (id: string) => {
  const getUser = useUsersStore(state => state.getUser)

  return useQuery({
    queryKey: ['user', id],
    queryFn: () => getUser(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateUser = () => {
  const createUser = useUsersStore(state => state.createUser)

  return useMutation({
    mutationFn: (params: CreateUserParams) => createUser(params),
    onSuccess: () => {}
  })
}

// Hook for updating a user
export const useUpdateUser = () => {
  const updateUser = useUsersStore(state => state.updateUser)

  return useMutation({
    mutationFn: ({ userId, params }: { userId: string; params: UpdateUserParams }) => updateUser(userId, params),
    onSuccess: () => {}
  })
}

// Hook for deleting a user
export const useDeleteUser = () => {
  const deleteUser = useUsersStore(state => state.deleteUser)

  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {}
  })
}

// Hook for bulk deleting users
export const useBulkDeleteUsers = () => {
  const bulkDeleteUsers = useUsersStore(state => state.bulkDeleteUsers)
  const deselectAllUsers = useUsersStore(state => state.deselectAllUsers)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteUsers(ids),
    onSuccess: response => {
      deselectAllUsers()
      handleBulkDeleteSuccess(response)
    }
  })
}

// Hook for sending email verification
export const useSendEmailVerification = () => {
  const sendEmailVerification = useUsersStore(state => state.sendEmailVerification)

  return useMutation({
    mutationFn: ({ userId, params }: { userId: string; params: SendEmailVerificationParams }) =>
      sendEmailVerification(userId, params),
    onSuccess: () => {}
  })
}
